import { View } from "@-/com.npmjs.react-native";
import { toast } from "@-/com.npmjs.sonner";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import ensureError from "@-/com.npmjs.ensure-error";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@-/dev.improve.haniverse.components.ui.alert-dialog";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  buildActorDataExport,
  buildActorDataExportFilename,
  downloadActorDataExport,
} from "@-/dev.improve.haniverse.lib.database.export";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import type { AnySQLiteDatabase } from "@-/com.npmjs.expo-sqlite";

/**
 * Shown before a SIGN-IN when the device holds local study data.
 *
 * Signing into an existing account makes the cloud the source of truth, so whatever
 * was studied signed-out is replaced. That has always been the behaviour — it just
 * happened silently. This says so first, and offers a copy of the data on the way
 * out.
 *
 * Deliberately NOT a merge prompt. Merging local and remote decks means resolving
 * conflicts across FSRS scheduling state, review logs and soft-delete tombstones,
 * and a user signing in generally knows they have an account and hasn't built a
 * separate local deck they expect to keep. The export covers the rare case honestly
 * without inventing a conflict-resolution model.
 */
export interface SignInReplaceWarningDialogProps {
  /** Connection to read the about-to-be-replaced data from (for the export). */
  getConnection: () => Promise<AnySQLiteDatabase>;
  onCancel: () => void;
  /** Proceed with the sign-in. */
  onContinue: () => void;
  open: boolean;
}

export function SignInReplaceWarningDialog({
  getConnection,
  onCancel,
  onContinue,
  open,
}: SignInReplaceWarningDialogProps) {
  const exportMutation = useMutation({
    mutationFn: async () => {
      const connection = await getConnection();
      const exportedAt = new Date().toISOString();
      const { ndjson, rowCount } = await buildActorDataExport(connection, { exportedAt });
      downloadActorDataExport(ndjson, buildActorDataExportFilename(exportedAt));
      return rowCount;
    },
    mutationKey: ["actor-data-export"],
    onError: (error) => {
      logger(import.meta.location).error("[sign-in] export failed", error);
      toast.error(`Could not export your data: ${ensureError(error).message}`);
    },
    onSuccess: (rowCount) => {
      toast.success(`Exported ${rowCount} row${rowCount === 1 ? "" : "s"}.`);
    },
  });

  return (
    <AlertDialog
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onCancel();
        }
      }}
      open={open}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <View className="flex-row items-center gap-2">
            <Icon className="text-destructive" name="LuAlertTriangle" size={22} />
            <AlertDialogTitle>Signing in replaces this device&rsquo;s data</AlertDialogTitle>
          </View>
          <AlertDialogDescription>
            You have study data saved on this device. Signing in loads your account&rsquo;s data
            from the cloud and replaces it — the two are not merged. Download a copy first if you
            want to keep it.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button
            disabled={exportMutation.isPending}
            onPress={() => {
              exportMutation.mutate();
            }}
            testID="sign-in-export-button"
            variant="outline"
          >
            <Text>{exportMutation.isPending ? "Exporting…" : "Download a copy"}</Text>
          </Button>
          <AlertDialogCancel testID="sign-in-replace-cancel-button">
            <Text>Cancel</Text>
          </AlertDialogCancel>
          <AlertDialogAction onPress={onContinue} testID="sign-in-replace-continue-button">
            <Text>Sign in anyway</Text>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
